import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { CheckCircle2, Loader2, Hourglass, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");
  const fired = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          if (!fired.current) {
            fired.current = true;
            confetti({
              particleCount: 180,
              spread: 80,
              origin: { y: 0.6 },
              colors: ["#00A896", "#FFC43D", "#EF476F", "#A8DADC"],
            });
          }
          return;
        }
      } catch { /* on réessaie */ }
      if (++attempts < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5 text-cream">
      <div
        data-testid="cpmc-payment-success-page"
        className="w-full max-w-lg rounded-3xl border border-cream/10 bg-cream/[0.05] p-10 text-center backdrop-blur"
      >
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto mb-6 h-14 w-14 animate-spin text-sun" />
            <h1 className="font-display text-2xl font-bold">Confirmation du paiement…</h1>
            <p className="mt-3 text-sm text-cream/60">
              Nous vérifions votre transaction auprès de Stripe.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-6 h-16 w-16 text-teal" />
            <h1 data-testid="cpmc-payment-confirmed-title" className="font-display text-3xl font-bold">
              Paiement confirmé !
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-cream/70 sm:text-base">
              Bienvenue dans la première cohorte francophone de la formation CPMC.
              Vous recevrez vos accès et tous les détails par courriel avant le 1er octobre 2026.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Hourglass className="mx-auto mb-6 h-14 w-14 text-sun" />
            <h1 className="font-display text-2xl font-bold">Paiement en traitement</h1>
            <p className="mt-3 text-sm text-cream/60">
              La confirmation prend un peu plus de temps. Vous recevrez un courriel
              dès que le paiement sera validé.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto mb-6 h-14 w-14 text-coral" />
            <h1 className="font-display text-2xl font-bold">Session introuvable</h1>
            <p className="mt-3 text-sm text-cream/60">
              Impossible de vérifier cette session de paiement.
            </p>
          </>
        )}
        <a
          href="/"
          data-testid="cpmc-payment-back-home"
          className="mt-8 inline-block rounded-full bg-sun px-6 py-3 font-semibold text-ink transition-colors duration-300 hover:bg-teal hover:text-cream"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  );
}
